import React, { useState, useEffect } from 'react';
import { User as UserIcon, Mail, Phone, Lock, Camera, Loader2, CheckCircle2, AlertCircle, Save, ShieldCheck, Zap } from 'lucide-react';
import { apiService, UserProfile, UserProfileUpdateRequest } from '../services/api';
import { useAuth } from '../hooks/useAuth';

const Profile: React.FC = () => {
    const { user, refreshUser } = useAuth();
    const [profileData, setProfileData] = useState<UserProfile | null>(null);
    const [updating, setUpdating] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);

    useEffect(() => {
        if (user) {
            setProfileData(user);
            setImagePreview(user.profile_image || null);
        }
    }, [user]);

    const handleProfileSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setUpdating(true);
        setMessage(null);

        const formData = new FormData(e.currentTarget);
        const updateRequest: UserProfileUpdateRequest = {
            email: formData.get('email') as string,
            first_name: formData.get('first_name') as string,
            last_name: formData.get('last_name') as string,
            phone: formData.get('phone') as string,
        };

        const imageFile = formData.get('profile_image') as File;
        if (imageFile && imageFile.size > 0) {
            updateRequest.profile_image = imageFile;
        }

        try {
            await apiService.updateCurrentUserProfile(updateRequest);
            await refreshUser();
            setMessage({ type: 'success', text: 'Profile updated successfully!' });
        } catch (err: unknown) {
            const error = err as any;
            console.error('Failed to update profile:', error);
            setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to update profile' });
        } finally {
            setUpdating(false);
        }
    };

    const handlePasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setChangingPassword(true);
        setMessage(null);

        const formData = new FormData(e.currentTarget);
        const old_password = formData.get('old_password') as string;
        const new_password = formData.get('new_password') as string;
        const new_password_confirm = formData.get('new_password_confirm') as string;

        if (new_password !== new_password_confirm) {
            setMessage({ type: 'error', text: 'New passwords do not match' });
            setChangingPassword(false);
            return;
        }

        try {
            await apiService.changePassword({ old_password, new_password, new_password_confirm });
            setMessage({ type: 'success', text: 'Password changed successfully!' });
            (e.target as HTMLFormElement).reset();
        } catch (err: unknown) {
            const error = err as any;
            console.error('Failed to change password:', error);
            setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to change password' });
        } finally {
            setChangingPassword(false);
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    if (!profileData) return null;

    return (
        <div className="max-w-6xl mx-auto p-6 space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-4xl font-black text-gray-900 tracking-tight">Account Settings</h1>
                    <p className="text-gray-500 mt-1 font-medium">Manage your personal information and security preferences</p>
                </div>
                {message && (
                    <div className={`flex items-center gap-3 px-6 py-3 rounded-2xl border ${message.type === 'success' ? 'bg-green-50 border-green-100 text-green-700' : 'bg-red-50 border-red-100 text-red-700'
                        } animate-in slide-in-from-top duration-300 font-bold text-sm shadow-sm`}>
                        {message.type === 'success' ? <CheckCircle2 className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
                        {message.text}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left: Profile Information */}
                <div className="lg:col-span-2 space-y-8">
                    <form onSubmit={handleProfileSubmit} className="bg-white rounded-[2rem] shadow-xl shadow-gray-100 border border-gray-100 overflow-hidden group">
                        <div className="px-10 py-8 border-b border-gray-100 bg-gray-50/30 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="h-10 w-10 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center">
                                    <UserIcon className="h-5 w-5" />
                                </div>
                                <h2 className="text-xl font-bold text-gray-900">Personal Information</h2>
                            </div>
                            <button
                                type="submit"
                                disabled={updating}
                                className="inline-flex items-center px-6 py-2.5 bg-orange-600 text-white rounded-xl font-bold text-sm hover:bg-orange-700 transition-all shadow-lg shadow-orange-100 disabled:opacity-50"
                            >
                                {updating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                                Save Changes
                            </button>
                        </div>

                        <div className="p-10 space-y-10">
                            {/* Profile Image Upload */}
                            <div className="flex flex-col sm:flex-row items-center gap-8">
                                <div className="relative group/avatar">
                                    <div className="h-32 w-32 rounded-[2.5rem] bg-gray-100 border-4 border-white shadow-xl overflow-hidden group-hover/avatar:scale-105 transition-transform duration-500">
                                        {imagePreview ? (
                                            <img src={imagePreview} alt="Profile" className="h-full w-full object-cover" />
                                        ) : (
                                            <div className="h-full w-full flex items-center justify-center text-gray-300">
                                                <UserIcon className="h-16 w-16" />
                                            </div>
                                        )}
                                    </div>
                                    <label className="absolute -bottom-2 -right-2 h-10 w-10 bg-white border border-gray-100 rounded-2xl flex items-center justify-center cursor-pointer shadow-lg hover:bg-orange-50 hover:text-orange-600 transition-all text-gray-400 group-hover/avatar:scale-110">
                                        <Camera className="h-5 w-5" />
                                        <input type="file" name="profile_image" className="hidden" accept="image/*" onChange={handleImageChange} />
                                    </label>
                                </div>
                                <div className="flex-1 text-center sm:text-left space-y-1">
                                    <h3 className="text-lg font-bold text-gray-900">Profile Photo</h3>
                                    <p className="text-sm text-gray-400 font-medium">Click the camera icon to upload a new avatar. JPG or PNG, max 2MB.</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                <div className="space-y-1.5">
                                    <label className="block text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Username</label>
                                    <div className="relative">
                                        <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <input
                                            value={profileData.username}
                                            disabled
                                            className="w-full bg-gray-50 border border-gray-100 rounded-2xl pl-12 pr-4 py-3.5 text-sm font-bold text-gray-400 cursor-not-allowed"
                                        />
                                    </div>
                                    <p className="text-[10px] text-gray-300 ml-1 italic">Username cannot be changed</p>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="block text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Account Role</label>
                                    <div className="relative">
                                        <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <input
                                            value={profileData.role.replace('_', ' ').toUpperCase()}
                                            disabled
                                            className="w-full bg-gray-50 border border-gray-100 rounded-2xl pl-12 pr-4 py-3.5 text-sm font-black text-orange-600/60 cursor-not-allowed"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="block text-xs font-black text-gray-400 uppercase tracking-widest ml-1">First Name</label>
                                    <input
                                        name="first_name"
                                        defaultValue={profileData.first_name}
                                        className="w-full bg-gray-50/50 border border-gray-200 rounded-2xl px-5 py-3.5 text-sm font-bold text-gray-900 focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none transition-all focus:bg-white"
                                        placeholder="Enter your first name"
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <label className="block text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Last Name</label>
                                    <input
                                        name="last_name"
                                        defaultValue={profileData.last_name}
                                        className="w-full bg-gray-50/50 border border-gray-200 rounded-2xl px-5 py-3.5 text-sm font-bold text-gray-900 focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none transition-all focus:bg-white"
                                        placeholder="Enter your last name"
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <label className="block text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Email Address</label>
                                    <div className="relative">
                                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <input
                                            name="email"
                                            type="email"
                                            defaultValue={profileData.email}
                                            className="w-full bg-gray-50/50 border border-gray-200 rounded-2xl pl-12 pr-4 py-3.5 text-sm font-bold text-gray-900 focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none transition-all focus:bg-white"
                                            placeholder="Enter your email"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="block text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Phone Number</label>
                                    <div className="relative">
                                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <input
                                            name="phone"
                                            defaultValue={profileData.phone}
                                            className="w-full bg-gray-50/50 border border-gray-200 rounded-2xl pl-12 pr-4 py-3.5 text-sm font-bold text-gray-900 focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none transition-all focus:bg-white"
                                            placeholder="Enter your phone number"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>

                {/* Right: Security Settings */}
                <div className="space-y-8">
                    <form onSubmit={handlePasswordSubmit} className="bg-white rounded-[2rem] shadow-xl shadow-gray-100 border border-gray-100 overflow-hidden">
                        <div className="px-8 py-6 border-b border-gray-100 bg-gray-50/30 flex items-center gap-4">
                            <div className="h-10 w-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center">
                                <Lock className="h-5 w-5" />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900">Security</h2>
                        </div>
                        <div className="p-8 space-y-6">
                            <div className="space-y-1.5">
                                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Current Password</label>
                                <input
                                    name="old_password"
                                    type="password"
                                    required
                                    className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-5 py-3.5 text-sm font-mono focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                                />
                            </div>

                            <div className="h-px bg-gray-100 my-2"></div>

                            <div className="space-y-1.5">
                                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest ml-1">New Password</label>
                                <input
                                    name="new_password"
                                    type="password"
                                    required
                                    className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-5 py-3.5 text-sm font-mono focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                                />
                            </div>

                            <div className="space-y-1.5">
                                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Confirm New Password</label>
                                <input
                                    name="new_password_confirm"
                                    type="password"
                                    required
                                    className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-5 py-3.5 text-sm font-mono focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={changingPassword}
                                className="w-full inline-flex items-center justify-center px-6 py-4 bg-gray-900 text-white rounded-2xl font-bold text-sm hover:bg-black transition-all shadow-xl shadow-gray-200 disabled:opacity-50"
                            >
                                {changingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                                Update Password
                            </button>
                        </div>
                    </form>

                    {/* Quick Info Bar */}
                    <div className="bg-gradient-to-tr from-orange-600 to-orange-400 rounded-[2rem] p-8 text-white shadow-2xl shadow-orange-100 flex flex-col gap-6 relative overflow-hidden group">
                        <Zap className="absolute -right-8 -bottom-8 h-48 w-48 text-white/10 rotate-12 group-hover:rotate-45 transition-transform duration-1000" />
                        <div className="relative z-10">
                            <div className="text-orange-100 text-[10px] font-black uppercase tracking-[0.2em] mb-2">Account ID</div>
                            <div className="text-sm font-mono font-bold break-all bg-white/10 p-4 rounded-2xl backdrop-blur-md border border-white/20">
                                {profileData.id}
                            </div>
                        </div>
                        <div className="relative z-10">
                            <div className="text-orange-100 text-[10px] font-black uppercase tracking-[0.2em] mb-1">Joined On</div>
                            <div className="text-lg font-bold">
                                {new Date(profileData.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;
